<template>
  <div class="max-w-5xl shadow entry-overview flex overflow-hidden bg-white sm:rounded-lg">
    <!-- Content area -->
    <div class="entry-detail w-full">
      <div class="flex max-w-4xl flex-col">
        <main class="flex-1">
          <div class="relative mx-auto max-w-4xl md:px-8">
            <div class="pt-10 pb-16">
              <div class="px-4 sm:px-6 md:px-0">
                <p class="text-sm">
                  Project Plan <span class="text-gray-400">→ {{ project.name }}</span>
                </p>
                <h1 class="text-3xl font-bold tracking-tight text-gray-900">
                  {{ proposal.title }}
                </h1>
              </div>
              <div class="px-4 sm:px-6 md:px-0">
                <div class="py-6">
                  <!-- Tabs -->
                  <div class="lg:hidden">
                    <label
                      for="selected-tab"
                      class="sr-only">
                      Select a tab
                    </label>
                    <select
                      id="selected-tab"
                      name="selected-tab"
                      class="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-purple-500 focus:outline-none focus:ring-purple-500 sm:text-sm">
                      <option
                        v-for="tab in tabs"
                        :key="tab.name"
                        :selected="(tab.name === 'Trip info')">
                        {{ tab.name }}
                      </option>
                    </select>
                  </div>
                  <div class="hidden lg:block">
                    <div class="border-b border-gray-200">
                      <nav class="-mb-px flex space-x-8">
                        <Link
                          v-for="tab in tabs"
                          :key="tab.name"
                          :href="tab.route ? route(tab.route) : tab.href"
                          :class="[(tab.name === 'Trip info') ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700', 'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm']">
                          {{ tab.name }}
                        </Link>
                      </nav>
                    </div>
                  </div>

                  <!-- First box -->
                  <div class="relative mt-6 max-w-5xl mx-auto">
                    <div class="mt-1 grid grid-cols-1 gap-8 sm:grid-cols-2">
                      <div class="sm:col-span-1">
                        <dt class="text-sm font-medium text-font">
                          Client
                        </dt>
                        <dd class="mt-1 text-sm text-font-darkest">
                          <p>
                            {{ project.client.name }}
                          </p>
                        </dd>
                      </div>
                      <div class="sm:col-span-1">
                        <dt class="text-sm font-medium text-font">
                          Project manager
                        </dt>
                        <dd class="mt-1 text-sm text-font-darkest">
                          <p>
                            {{ project.manager.name }}
                          </p>
                        </dd>
                      </div>
                      <div class="sm:col-span-1">
                        <dt class="text-sm font-medium text-font">
                          Destinations
                        </dt>
                        <dd class="mt-1 text-sm text-font-darkest">
                          <span
                            v-for="(place, index) in proposal.places"
                            :key="place.id">
                            {{ place.name }}<span v-if="(index+1 < proposal.places.length)">, </span>
                          </span>
                        </dd>
                      </div>
                      <div class="sm:col-span-1">
                        <dt class="text-sm font-medium text-font">
                          Travel dates
                        </dt>
                        <dd class="mt-1 text-sm text-font-darkest">
                          <formatted-date-range
                            :from="proposal.start_date"
                            :to="proposal.end_date" />
                        </dd>
                      </div>
                    </div>
                  </div>

                  <!-- Description list with inline editing -->
                  <div class="mt-10 divide-y divide-gray-200">
                    <div class="space-y-1">
                      <h3 class="text-lg font-medium leading-6 text-gray-900">
                        Project Plan access
                      </h3>
                      <p class="max-w-2xl text-sm text-gray-500">
                        Rebuild the access URLs or turn them off
                      </p>
                    </div>
                    <div class="mt-6">
                      <dl class="divide-y divide-gray-200">
                        <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
                          <dt class="text-sm font-medium text-gray-500">
                            Client access URL
                          </dt>
                          <dd class="mt-1 flex text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                            <span class="flex-grow">
                              <Link
                                class="text-purple-700 underline hover:text-purple-500"
                                :href="route('design.project_plan.client')">//client/31bx29hx23</Link>
                            </span>
                            <span class="ml-4 flex-shrink-0">
                              <button
                                type="button"
                                class="rounded-md bg-white font-medium text-purple-600 hover:text-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2">
                                Rebuild URL
                              </button>
                            </span>
                          </dd>
                        </div>
                        <SwitchGroup
                          as="div"
                          class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:pt-5">
                          <SwitchLabel
                            as="dt"
                            class="text-sm font-medium text-gray-500"
                            passive>
                            Client access
                          </SwitchLabel>
                          <dd class="mt-1 flex text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                            <Switch
                              v-model="accessClient"
                              :class="[accessClient ? 'bg-purple-600' : 'bg-gray-200', 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 sm:ml-auto']">
                              <span
                                aria-hidden="true"
                                :class="[accessClient ? 'translate-x-5' : 'translate-x-0', 'inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                            </Switch>
                          </dd>
                        </SwitchGroup>
                        <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
                          <dt class="text-sm font-medium text-gray-500">
                            Tour Leader access URL
                          </dt>
                          <dd class="mt-1 flex text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                            <span class="flex-grow">
                              <Link
                                class="text-purple-700 underline hover:text-purple-500"
                                :href="route('design.project_plan.tour_manager')">//tour-manager/31bx29hx23</Link>
                            </span>
                            <span class="ml-4 flex-shrink-0">
                              <button
                                type="button"
                                class="rounded-md bg-white font-medium text-purple-600 hover:text-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2">
                                Rebuild URL
                              </button>
                            </span>
                          </dd>
                        </div>
                        <SwitchGroup
                          as="div"
                          class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-b sm:border-gray-200 sm:py-5">
                          <SwitchLabel
                            as="dt"
                            class="text-sm font-medium text-gray-500"
                            passive>
                            Tour Leader access
                          </SwitchLabel>
                          <dd class="mt-1 flex text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                            <Switch
                              v-model="accessTourManager"
                              :class="[accessTourManager ? 'bg-purple-600' : 'bg-gray-200', 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 sm:ml-auto']">
                              <span
                                aria-hidden="true"
                                :class="[accessTourManager ? 'translate-x-5' : 'translate-x-0', 'inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                            </Switch>
                          </dd>
                        </SwitchGroup>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script setup>
/* external */
import { ref } from 'vue';
import { Link } from '@inertiajs/vue3';
import FormattedDateRange from '@/ComponentsDeprecated/Time/FormattedDateRange';

/* local */
import { tabs, project, proposal } from './ProjectPlanDummy';

const accessClient = ref(false);
const accessTourManager = ref(true);

</script>
